import React, { useEffect, useContext, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";

import { makeStyles } from "@mui/styles";
import { Typography } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import {
    Snackbar,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    CircularProgress,
} from "@mui/material";

// Bootstrap
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

// My Files
import { UserContext } from "../../context";
import ToolbarWithLogo from "../ToolbarWithLogo";
import BackButton from "../BackButton";
import firebase from "../../firebase";
import AnnotationCanvas from "./AnnotationCanvas";

const useStyles = makeStyles((theme) => ({
    videoPlayer: {
        marginTop: 15,
        [theme.breakpoints.down("md")]: { marginTop: 0 },
    },
    columnPadding: {
        [theme.breakpoints.down("md")]: { paddingLeft: 0 },
        paddingRight: 0,
    },
    pageContainer: {
        paddingBottom: 65,
    },
    displayName: {
        flexGrow: 1,
        paddingTop: 10,
        fontSize: 24,
        [theme.breakpoints.down("md")]: { paddingLeft: 15, fontSize: 19 },
        overflowWrap: "break-word",
        maxWidth: "100%",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        textOverflow: "ellipsis",
    },
}));

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

export default function VideoAnnotationPage(props) {
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();

    const { currentUserData } = useContext(UserContext);

    const [capturedImage, setCapturedImage] = useState(null);
    const [playlistInfo, setPlaylistInfo] = useState(null);
    const [statusMessage, setStatusMessage] = useState("");
    const [statusType, setStatusType] = useState("success");
    const [statusIsVisible, setStatusIsVisible] = useState(false);

    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [annotationName, setAnnotationName] = useState("Annotation");
    const [annotationDuration, setAnnotationDuration] = useState(5);
    const [pendingAnnotationData, setPendingAnnotationData] = useState(null);
    const [pendingAnnotationJson, setPendingAnnotationJson] = useState(null);
    const [originalCapturedImage, setOriginalCapturedImage] = useState(null);

    const [isEditMode, setIsEditMode] = useState(false);
    const [editingClipKey, setEditingClipKey] = useState(null);
    const [initialAnnotationData, setInitialAnnotationData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
            if (!user) {
                history.replace("/signin?url=" + location.pathname);
            }
        });

        if (location.state && location.state.capturedImage) {
            setCapturedImage(location.state.capturedImage);
            setOriginalCapturedImage(location.state.capturedImage);
            setPlaylistInfo({
                playlistKey: location.state.playlistKey,
                playlistName: location.state.playlistName,
                currentTime: location.state.currentTime,
                videoKey: location.state.videoKey,
                clipIndex: location.state.clipIndex,
            });

            if (location.state.editMode && location.state.clipKey) {
                setIsEditMode(true);
                setEditingClipKey(location.state.clipKey);
                setAnnotationName(location.state.clipName || "Annotation");
                setAnnotationDuration(location.state.clipDuration || 5);
                setInitialAnnotationData(location.state.annotationData || null);

                if (location.state.originalImageUrl) {
                    setCapturedImage(location.state.originalImageUrl);
                    setOriginalCapturedImage(location.state.originalImageUrl);
                }
            }
        }

        return () => unsubscribe();
    }, [history, location]);

    const handleSaveAnnotation = async (
        annotatedImageDataUrl,
        annotationJsonData
    ) => {
        setPendingAnnotationData(annotatedImageDataUrl);
        setPendingAnnotationJson(annotationJsonData);
        setSaveDialogOpen(true);
    };

    const handleConfirmSave = async () => {
        if (!playlistInfo || !currentUserData || !pendingAnnotationData) {
            setStatusMessage("Missing information");
            setStatusType("error");
            setStatusIsVisible(true);
            return;
        }

        setSaveDialogOpen(false);
        setIsSaving(true); // Show loading indicator

        try {
            const annotatedResponse = await fetch(pendingAnnotationData);
            const annotatedBlob = await annotatedResponse.blob();

            const timestamp = Date.now();
            const storageRef = firebase.storage().ref();

            let annotatedDownloadURL;
            let annotatedFilename;
            let originalDownloadURL;
            let originalFilename;

            if (isEditMode && editingClipKey) {
                const playlistRef = firebase
                    .database()
                    .ref("playlists")
                    .child(playlistInfo.playlistKey);
                const clipRef = playlistRef
                    .child("clips")
                    .child(editingClipKey);
                const clipSnapshot = await clipRef.once("value");
                const existingClipData = clipSnapshot.val();

                if (!existingClipData) {
                    throw new Error("Existing clip not found");
                }

                originalDownloadURL = existingClipData.originalImageUrl;
                originalFilename = existingClipData.originalStoragePath;

                annotatedFilename = existingClipData.storagePath;
                const annotatedImageRef = storageRef.child(annotatedFilename);
                const annotatedUploadTask = await annotatedImageRef.put(
                    annotatedBlob,
                    { contentType: "image/jpeg" }
                );
                annotatedDownloadURL =
                    await annotatedUploadTask.ref.getDownloadURL();

                const updateData = {
                    imageUrl: annotatedDownloadURL,
                    name: annotationName,
                    duration: annotationDuration,
                };

                if (pendingAnnotationJson) {
                    updateData.annotationData = pendingAnnotationJson;
                }

                await clipRef.update(updateData);

                setStatusMessage("Annotation updated successfully!");
            } else {
                const originalImg = new Image();
                originalImg.src = originalCapturedImage;

                await new Promise((resolve, reject) => {
                    originalImg.onload = resolve;
                    originalImg.onerror = reject;
                });

                const canvas = document.createElement("canvas");
                canvas.width = originalImg.width;
                canvas.height = originalImg.height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(originalImg, 0, 0);

                const originalBlob = await new Promise((resolve) => {
                    canvas.toBlob(resolve, "image/jpeg", 0.92);
                });

                annotatedFilename = `annotations/${playlistInfo.playlistKey}/${timestamp}_annotated.jpg`;
                const annotatedImageRef = storageRef.child(annotatedFilename);
                const annotatedUploadTask = await annotatedImageRef.put(
                    annotatedBlob,
                    { contentType: "image/jpeg" }
                );
                annotatedDownloadURL =
                    await annotatedUploadTask.ref.getDownloadURL();

                originalFilename = `annotations/${playlistInfo.playlistKey}/${timestamp}_original.jpg`;
                const originalImageRef = storageRef.child(originalFilename);
                const originalUploadTask = await originalImageRef.put(
                    originalBlob,
                    { contentType: "image/jpeg" }
                );
                originalDownloadURL =
                    await originalUploadTask.ref.getDownloadURL();

                const playlistRef = firebase
                    .database()
                    .ref("playlists")
                    .child(playlistInfo.playlistKey);

                const playlistSnapshot = await playlistRef.once("value");
                const playlistData = playlistSnapshot.val();

                if (!playlistData) {
                    throw new Error("Playlist not found");
                }

                const videoKey = playlistData.video;

                const clips = playlistData.clips || {};
                const clipArray = Object.values(clips);
                const maxIndex =
                    clipArray.length > 0
                        ? Math.max(...clipArray.map((c) => c.index))
                        : -1;

                const newClipRef = playlistRef.child("clips").push();

                const newClipData = {
                    type: "annotation",
                    imageUrl: annotatedDownloadURL,
                    originalImageUrl: originalDownloadURL,
                    storagePath: annotatedFilename,
                    originalStoragePath: originalFilename,
                    index: maxIndex + 1,
                    name: annotationName,
                    note: `Created at ${Math.floor(
                        playlistInfo.currentTime || 0
                    )}s`,
                    stars: 0,
                    sourceTime: playlistInfo.currentTime || 0,
                    duration: annotationDuration,
                };

                if (pendingAnnotationJson) {
                    newClipData.annotationData = pendingAnnotationJson;
                }

                if (videoKey) {
                    newClipData.video = videoKey;
                }

                await newClipRef.set(newClipData);

                setStatusMessage("Annotation saved successfully!");
            }

            setStatusType("success");
            setStatusIsVisible(true);

            setAnnotationName("Annotation");
            setAnnotationDuration(5);
            setPendingAnnotationData(null);
            setPendingAnnotationJson(null);

            setTimeout(() => {
                history.push({
                    pathname: "/playlist",
                    search: `?key=${playlistInfo.playlistKey}`,
                });
            }, 1500);
        } catch (error) {
            console.error("Error saving annotation:", error);
            setStatusMessage("Failed to save annotation: " + error.message);
            setStatusType("error");
            setStatusIsVisible(true);
        } finally {
            setIsSaving(false); // Hide loading indicator
        }
    };

    const handleCancelSave = () => {
        setSaveDialogOpen(false);
        setPendingAnnotationData(null);
        setAnnotationName("Annotation");
        setAnnotationDuration(5);
    };

    const handleStatusClose = () => {
        setStatusIsVisible(false);
    };

    return (
        <>
            <BackButton />
            <Container fluid className={classes.pageContainer}>
                <Row>
                    <Col>
                        {playlistInfo && (
                            <Typography className={classes.displayName}>
                                {isEditMode ? "Editing: " : "Annotating: "}
                                {playlistInfo.playlistName}
                            </Typography>
                        )}
                        {capturedImage ? (
                            <AnnotationCanvas
                                capturedImage={capturedImage}
                                onSave={handleSaveAnnotation}
                                initialAnnotationData={initialAnnotationData}
                                playlistKey={playlistInfo?.playlistKey}
                            />
                        ) : (
                            <Typography variant="body1" sx={{ p: 2 }}>
                                No image captured. Please go back and capture a
                                frame from the video.
                            </Typography>
                        )}
                    </Col>
                </Row>
            </Container>

            <Dialog
                open={saveDialogOpen}
                onClose={handleCancelSave}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Save Annotation</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Annotation Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={annotationName}
                        onChange={(e) => setAnnotationName(e.target.value)}
                        sx={{ mb: 2, mt: 1 }}
                    />
                    <TextField
                        margin="dense"
                        label="Display Duration (seconds)"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={annotationDuration}
                        onChange={(e) =>
                            setAnnotationDuration(
                                Math.max(1, parseInt(e.target.value) || 5)
                            )
                        }
                        inputProps={{ min: 1, max: 60 }}
                        helperText="How long should this annotation be displayed?"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelSave}>Cancel</Button>
                    <Button
                        onClick={handleConfirmSave}
                        variant="contained"
                        color="primary"
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={isSaving}
                disableEscapeKeyDown
                aria-labelledby="saving-dialog-title"
            >
                <DialogContent>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                            py: 2,
                        }}
                    >
                        <CircularProgress size={60} />
                        <Typography variant="h6">
                            Saving annotation...
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Please wait while your annotation is being saved
                        </Typography>
                    </Box>
                </DialogContent>
            </Dialog>
            <Snackbar
                open={statusIsVisible}
                autoHideDuration={6000}
                onClose={handleStatusClose}
            >
                <Alert onClose={handleStatusClose} severity={statusType}>
                    {statusMessage}
                </Alert>
            </Snackbar>
            <ToolbarWithLogo />
        </>
    );
}
